import { generatedApi } from './api.generated';
import {
  createEndpointExports,
  createHookExports,
  provideListTags,
  LIST_TAG_ID,
} from '@skynet/data-helpers';
import type { HookExports } from '@skynet/data-helpers';
import { DEX_BLOCKCHAINS_API_ENDPOINTS as cfg } from './config';

/**
 * Cache tags follow the recommended `id: 'LIST'` recipe:
 * https://redux-toolkit.js.org/rtk-query/usage/automated-refetching#recipes
 *
 * A single `Blockchains` tag type covers both the collection (LIST) and each
 * individual blockchain (per-id tag), mirroring the DEX Vaults API: mutating
 * one blockchain (update/delete/changeBlockchainNode/switchBlockchainPrivateRPC) invalidates only
 * that blockchain's tag, refetching its detail query and the list (which
 * provides the same per-item tag); creating a blockchain invalidates the
 * `LIST` tag to refetch the collection without touching unrelated entities.
 *
 * `reloadApp` restarts the backend process and does not change any
 * blockchain's data, so it neither provides nor invalidates tags.
 */
const enhancedDexBlockchainsApi = generatedApi.enhanceEndpoints({
  addTagTypes: ['Blockchains'],
  endpoints: {
    [cfg.listBlockchains.generated]: {
      providesTags: (result) => provideListTags(result?.data, 'Blockchains'),
    },
    [cfg.getBlockchain.generated]: {
      providesTags: (_result, _error, arg) => [{ type: 'Blockchains', id: arg.blockchainId }],
    },
    [cfg.createBlockchain.generated]: {
      invalidatesTags: [{ type: 'Blockchains', id: LIST_TAG_ID }],
    },
    [cfg.updateBlockchain.generated]: {
      invalidatesTags: (_result, _error, arg) => [{ type: 'Blockchains', id: arg.blockchainId }],
    },
    [cfg.deleteBlockchain.generated]: {
      invalidatesTags: (_result, _error, arg) => [{ type: 'Blockchains', id: arg.blockchainId }],
    },
    [cfg.changeBlockchainNode.generated]: {
      invalidatesTags: (_result, _error, arg) => [{ type: 'Blockchains', id: arg.blockchainId }],
    },
    [cfg.switchBlockchainPrivateRPC.generated]: {
      invalidatesTags: (_result, _error, arg) => [{ type: 'Blockchains', id: arg.blockchainId }],
    },
  },
});

export const dexBlockchainsApi: typeof enhancedDexBlockchainsApi = enhancedDexBlockchainsApi;

export const hookExports: HookExports<typeof cfg, typeof dexBlockchainsApi> = createHookExports(
  cfg,
  dexBlockchainsApi,
);

const endpointExports = createEndpointExports(cfg, dexBlockchainsApi);

export const dexBlockchainsApiEndpoints: typeof endpointExports = endpointExports;
